/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GsptService } from './gspt.service';
import { GsptDialogComponent, GsptDialogResult } from './gspt-dialog.component';
import { distinctUntilChanged, filter, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LoggerService } from '../../modules/shared/services/logger.service';

@Component({
    selector: 'dash-gis-gspt',
    templateUrl: './gspt.component.html',
    providers: [GsptService],
})
export class GsptComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public layerName: string;
    @Input() public mapName: string;
    @Input() public node: any;

    @ViewChild('spinner')
    public spinnerTemplate: TemplateRef<any>;

    public isGsptEnabled$: Observable<boolean>;

    private mapName$ = new BehaviorSubject<string>(null);
    private layerName$ = new BehaviorSubject<string>(null);
    private dispose$ = new Subject<void>();

    constructor(private logger: LoggerService, private gsptService: GsptService, private dialog: MatDialog) {}

    ngOnInit(): void {
        this.isGsptEnabled$ = this.gsptService.isGsptEnabled$(
            this.mapName$.asObservable().pipe(distinctUntilChanged()),
            this.layerName$.asObservable().pipe(distinctUntilChanged()),
        ).pipe(
            takeUntil(this.dispose$),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.mapName) {
            this.mapName$.next(changes.mapName.currentValue);
        }
        if (changes.layerName) {
            this.layerName$.next(changes.layerName.currentValue);
        }
    }

    ngOnDestroy() {
        this.dispose$.next();
    }

    public showDialog() {
        const dialogRef = this.dialog.open(GsptDialogComponent, {
            disableClose: true,
        });
        let spinnerRef;
        const selected$ = this.gsptService.getSelectedFeature(this.mapName, this.layerName);
        dialogRef
            .afterClosed()
            .pipe(
                filter((b) => b),
                tap(() => {
                    spinnerRef = this.dialog.open(this.spinnerTemplate);
                }),
                withLatestFrom(selected$),
                switchMap(([result, node]: [GsptDialogResult, any]) =>
                    this.gsptService.query(this.mapName, this.layerName, node, result.distance, result.direction)
                )
            )
            .subscribe(
                () => spinnerRef.close(),
                (err) => this.logger.error(err)
            );
    }
}
